// Social feed configuration — fictional authors and text templates
// (FC 25 pattern: the club's progress narrated as a social network,
// with template variety so posts don't repeat).

import type { NewsItem } from '@/hooks/useClubNews'

export type FeedAuthorKind = 'media' | 'fan' | 'rival'

export interface FeedAuthor {
  id: string
  name: string
  handle: string
  kind: FeedAuthorKind
}

export const FEED_AUTHORS: FeedAuthor[] = [
  { id: 'padel-press', name: 'Padel Press', handle: '@padelpress', kind: 'media' },
  { id: 'marca-regional', name: 'Deporte Regional', handle: '@dep_regional', kind: 'media' },
  { id: 'la-bandeja', name: 'La Bandeja Diaria', handle: '@labandeja', kind: 'media' },
  { id: 'fan-pepe', name: 'Pepe del 4', handle: '@pepedel4', kind: 'fan' },
  { id: 'fan-marta', name: 'Marta Volea', handle: '@martavolea', kind: 'fan' },
  { id: 'fan-rulo', name: 'Rulo Padelero', handle: '@rulopadel', kind: 'fan' },
  { id: 'rival-dt', name: 'DT Rival', handle: '@dt_rival', kind: 'rival' },
  { id: 'insider', name: 'Padel Insider', handle: '@padelinsider', kind: 'media' },
]

/**
 * Templates per news category and author kind. `{text}` is replaced
 * with the original headline. 3+ variants per combination for variety.
 */
const TEMPLATES: Record<NewsItem['category'], Record<FeedAuthorKind, string[]>> = {
  economy: {
    media: [
      'INFORME · {text}',
      'Los numeros del club: {text}',
      'Balance al dia: {text}',
    ],
    fan: [
      'Se nota la buena gestion... {text} 💪',
      'Ojala lo inviertan en mas pistas: {text}',
      'Mi club va como un tiro: {text}',
    ],
    rival: [
      'Mucho ruido por poco: {text}',
      'Ya veremos si dura: {text}',
      'No esta mal... para un club pequeño: {text}',
    ],
  },
  weather: {
    media: [
      'METEO · {text}',
      'Parte del tiempo: {text}',
      'Asi amanece la jornada: {text}',
    ],
    fan: [
      '{text}... pues nada, a la indoor 🌧️',
      'Dia perfecto para unas bandejas: {text}',
      '{text} — quien tenga techo que lo disfrute',
    ],
    rival: [
      'El tiempo iguala a todos: {text}',
      'A ver como gestionan esto: {text}',
      'Nosotros entrenamos igual: {text}',
    ],
  },
  staff: {
    media: [
      'FICHAJE DE PERSONAL · {text}',
      'Movimiento en plantilla tecnica: {text}',
      'El club refuerza su equipo: {text}',
    ],
    fan: [
      'Buen refuerzo: {text} 👏',
      'Por fin invierten en personal: {text}',
      'Esto profesionaliza el club: {text}',
    ],
    rival: [
      'Contratan para tapar carencias: {text}',
      'Veremos si rinde: {text}',
      'Tomamos nota: {text}',
    ],
  },
  facilities: {
    media: [
      'INSTALACIONES · {text}',
      'Estado del recinto: {text}',
      'Asi estan las pistas: {text}',
    ],
    fan: [
      'Porfa arreglad esto: {text} 🙏',
      'Lo vi ayer y si... {text}',
      'Aviso a mantenimiento: {text}',
    ],
    rival: [
      'En nuestro club esto no pasa: {text}',
      'Cuidado con el desgaste: {text}',
      'Detalle revelador: {text}',
    ],
  },
  tournament: {
    media: [
      'COMPETICION · {text}',
      'CRONICA · {text}',
      'Lo deportivo manda: {text}',
    ],
    fan: [
      'VAMOOOS 🎾 {text}',
      'Que orgullo de club: {text}',
      'Ahi estaremos animando: {text}',
    ],
    rival: [
      'Suerte... la van a necesitar: {text}',
      'Nos vemos en la pista: {text}',
      'Interesante resultado: {text}',
    ],
  },
  milestone: {
    media: [
      'HITO · {text}',
      'El ascenso de un club: {text}',
      'Historia en marcha: {text}',
    ],
    fan: [
      'Lo vi nacer y miralo ahora 🥹 {text}',
      'ORGULLO TOTAL: {text}',
      'Somos grandes: {text}',
    ],
    rival: [
      'Crecen rapido... demasiado: {text}',
      'Respeto, aunque me cueste: {text}',
      'El listón sube: {text}',
    ],
  },
  effect: {
    media: [
      'ACTUALIDAD · {text}',
      'Situacion en el club: {text}',
      'Lo que afecta al dia a dia: {text}',
    ],
    fan: [
      'Esto se nota en las reservas: {text}',
      'Atentos a esto: {text}',
      'Ojito: {text}',
    ],
    rival: [
      'Les afecta mas de lo que dicen: {text}',
      'Todo cuenta en la liga: {text}',
      'Lo seguimos de cerca: {text}',
    ],
  },
  // Story posts always carry an explicit authorId and are shown verbatim, so
  // these templates are only a type-safety fallback (never normally hit).
  story: {
    media: ['{text}'],
    fan: ['{text}'],
    rival: ['{text}'],
  },
}

/** Deterministic 32-bit hash for stable author/template selection */
export function feedHash(value: string): number {
  let hash = 2166136261
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

export interface FeedPostData {
  id: string
  author: FeedAuthor
  body: string
  category: NewsItem['category']
}

/**
 * Deterministically dress a news item as a social post: same item +
 * day always produces the same author and template (no flicker
 * between renders), different items vary.
 */
export function composeFeedPost(item: NewsItem, day: number): FeedPostData {
  // Story beats name their author and are shown verbatim (pre-authored copy).
  if (item.authorId) {
    const author = FEED_AUTHORS.find(a => a.id === item.authorId) ?? FEED_AUTHORS[0]
    return { id: item.id, author, body: item.headline, category: item.category }
  }

  const hash = feedHash(`${item.id}-${day}`)
  const author = FEED_AUTHORS[hash % FEED_AUTHORS.length]
  const templates = TEMPLATES[item.category]?.[author.kind] ?? ['{text}']
  const template = templates[(hash >>> 4) % templates.length]

  return {
    id: item.id,
    author,
    body: template.replace('{text}', item.headline),
    category: item.category,
  }
}
